import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Container,
  InputAdornment,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";

import {
  textPrimary,
  textsecondary,
  lightBg2,
  primary,
  secondary,
  lightBg,
} from "../../constants/colors";
import { useAppContext } from "../../context/AppContext";
import { emailRegex, passwordRegex } from "../../constants/regex";
// import Logo from "../assets/Logo.png";
import Logo from "../../assets/logo.webp";
import landing_image_1 from "../../assets/landing_image_1.png";
import landing_image_2 from "../../assets/landing_image_2.webp";
import Toast from "../../components/toast";
import userService from "../../helpers/user.service";

// APIs
import { signup, signin, forgotPassword } from "../../helpers/Authentication";
import { flexDirection, width } from "@mui/system";

const MedicalChatbotApp = () => {
  const navigate = useNavigate();
  const { isMobile, isSmallMobile, updateUserData } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);

  const initialLoginFormValues = { email: "", password: "" };
  const initialSignupFormValues = {
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  };

  const [currentView, setCurrentView] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loginForm, setLoginForm] = useState(initialLoginFormValues);
  const [signupForm, setSignupForm] = useState(initialSignupFormValues);

  const [loginErrors, setLoginErrors] = useState({ email: "", password: "" });
  const [signupErrors, setSignupErrors] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [forgotErrors, setForgotErrors] = useState({ email: "" });

  // const handleSignUp = async () => {
  //   try {
  //     if (
  //       signupForm.name === "" ||
  //       signupForm.email === "" ||
  //       signupForm.password === "" ||
  //       signupForm.confirmPassword === ""
  //     ) {
  //       Toast.error("One or more fields are empty.");
  //       return;
  //     } else if (signupForm.password !== signupForm.confirmPassword) {
  //       Toast.error("Password did not match.");
  //       return;
  //     }
  //     else if (!passwordRegex.test(signupForm.password)) {
  //       Toast.error(
  //         "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.",
  //       );
  //       return;
  //     }
  //     else if (!emailRegex.test(signupForm.email)) {
  //       Toast.error("Enter a valid email address.");
  //       return;
  //     }
  //     setIsLoading(true);
  //     const payload = {
  //       fullName: signupForm.name,
  //       emailAddress: signupForm.email,
  //       password: signupForm.password,
  //       passwordConfirm: signupForm.confirmPassword,
  //     };
  //     const response = await signup(payload);
  //     if (response.statusCode === 200 && response.isSuccess === true) {
  //       Toast.success(response.message);
  //       setCurrentView("login");
  //     } else {
  //       Toast.error(response.message);
  //     }
  //     setIsLoading(false);
  //   } catch (error) {
  //     Toast.error(error);
  //     setIsLoading(false);
  //   }
  // };

  const handleSignUp = async () => {
    // --- Validate fields ---
    const errors = { name: "", email: "", password: "", confirmPassword: "" };
    let isValid = true;

    if (signupForm.name.trim() === "") {
      errors.name = "Name is required.";
      isValid = false;
    } else if (signupForm.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters.";
      isValid = false;
    }

    if (signupForm.email.trim() === "") {
      errors.email = "Email is required.";
      isValid = false;
    } else if (!emailRegex.test(signupForm.email)) {
      errors.email = "Enter a valid email address.";
      isValid = false;
    }

    if (signupForm.password.trim() === "") {
      errors.password = "Password is required.";
      isValid = false;
    } else if (!passwordRegex.test(signupForm.password)) {
      errors.password =
        "Min 8 chars, with uppercase, lowercase, number & special character.";
      isValid = false;
    }

    if (signupForm.confirmPassword.trim() === "") {
      errors.confirmPassword = "Please confirm your password.";
      isValid = false;
    } else if (signupForm.password !== signupForm.confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
      isValid = false;
    }

    setSignupErrors(errors);
    if (!isValid) return; // ← stops before API hit

    // --- API call ---
    try {
      setIsLoading(true);
      const payload = {
        fullName: signupForm.name,
        emailAddress: signupForm.email,
        password: signupForm.password,
        passwordConfirm: signupForm.confirmPassword,
      };
      const response = await signup(payload);
      if (response.statusCode === 200 && response.isSuccess === true) {
        Toast.success(response.message);
        setCurrentView("login");
      } else {
        Toast.error(response.message);
      }
      setIsLoading(false);
    } catch (error) {
      Toast.error(error);
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    // --- Validate fields ---
    const errors = { email: "", password: "" };
    let isValid = true;

    if (loginForm.email.trim() === "") {
      errors.email = "Email is required.";
      isValid = false;
    } else if (!emailRegex.test(loginForm.email)) {
      errors.email = "Enter a valid email address.";
      isValid = false;
    }

    if (loginForm.password.trim() === "") {
      errors.password = "Password is required.";
      isValid = false;
    } else if (loginForm.password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
      isValid = false;
    }

    setLoginErrors(errors);
    if (!isValid) return; // ← stops before API hit

    // --- API call ---
    try {
      setIsLoading(true);
      const payload = { email: loginForm.email, password: loginForm.password };
      const response = await signin(payload);

      if (response.statusCode === 200 && response.isSuccess === true) {
        userService.Set_User(response.data);
        if (response.data.User.roleID === "6a4cbf11742c7c931709101d") {
          window.location.href = "/physician/dashboard";
        } else if (response.data.User.roleID === "6a4cbece742c7c9317091019") {
          window.location.href = "/admin/dashboard";
        } else if (response.data.User.roleID === "6a4cbf18742c7c931709101f") {
          window.location.href = "/facilities/patients";
        } else {
          window.location.href = "/chat";
        }
      } else {
        Toast.error(response.message);
      }
      setIsLoading(false);
    } catch (error) {
      Toast.error(error);
      setIsLoading(false);
    }
  };

  // const handleSignIn = async () => {
  //   try {
  //     if (loginForm.email === "" || loginForm.password === "") {
  //       Toast.error("One or more fields are empty.");
  //       return;
  //     }

  //     const { email, password } = loginForm;

  //     // 🔥 Hardcoded check before API
  //     if (password === "12345678") {
  //       if (email === "physician@example.com") {
  //         window.location.href = "/physician/appointments";
  //         return;
  //       }

  //       if (email === "facilities@example.com") {
  //         window.location.href = "/facilities/patients";
  //         return;
  //       }

  //       if (email === "admin@example.com") {
  //         window.location.href = "/admin/dashboard";
  //         return;
  //       }
  //     }

  //     // ❌ If not matched, then call API
  //     setIsLoading(true);

  //     const payload = { email, password };
  //     const response = await signin(payload);

  //     if (response.statusCode === 200 && response.isSuccess === true) {
  //       userService.Set_User(response.data);
  //       window.location.href = "/chat";
  //     } else {
  //       Toast.error(response.message);
  //     }

  //     setIsLoading(false);

  //   } catch (error) {
  //     Toast.error(error);
  //     setIsLoading(false);
  //   }
  // };

  const handleForgotPassword = async () => {
    // --- Validate fields ---
    const errors = { email: "" };
    let isValid = true;

    if (loginForm.email.trim() === "") {
      errors.email = "Email is required.";
      isValid = false;
    } else if (!emailRegex.test(loginForm.email)) {
      errors.email = "Enter a valid email address.";
      isValid = false;
    }

    setForgotErrors(errors);
    if (!isValid) return; // ← stops before API hit

    // --- API call ---
    try {
      setIsLoading(true);
      const payload = { emailAddress: loginForm.email };
      const response = await forgotPassword(payload);
      if (response.statusCode === 200 && response.isSuccess === true) {
        Toast.success(response.message);
        setCurrentView("login");
      } else {
        Toast.error(response.message);
      }
      setIsLoading(false);
    } catch (error) {
      Toast.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userService.Get_User() !== null) {
      const user = userService.Get_User();
      if (user?.roleID === "6a4cbf11742c7c931709101d") {
        window.location.href = "/physician/dashboard";
      } else if (user?.roleID === "6a4cbece742c7c9317091019") {
        window.location.href = "/admin/dashboard";
      } else if (user?.roleID === "6a4cbf18742c7c931709101f") {
        window.location.href = "/facilities/patients";
      } else {
        window.location.href = "/chat";
      }
    }
    setLoginForm(initialLoginFormValues);
    setSignupForm(initialSignupFormValues);
    setShowPassword(false);
    setShowConfirmPassword(false);
  }, [currentView]);

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        maxHeight: "100vh",
        width: "100%",
      }}
    >
      {/* LEFT SIDE (Image / Promo) */}
      {!isMobile && (
        <Box
          sx={{
            width: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
            padding: "10px",
            borderRadius: "20px",
            position: "relative",
          }}
        >
          <Box
            component="img"
            src={landing_image_2}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "20px",
            }}
          />
          <Typography
            sx={{
              position: 'absolute',
              bottom: '30px',
              left: '30px',
              fontSize: { xs: "18px", md: "34px" },
              color: "#fff",
              fontWeight: 600,
              lineHeight: { xs: "22px", md: "40px" },
            }}>
            Support you can trust, <br />
            <span style={{ color: '#7B7DE5' }}>whenever you need it.</span>
          </Typography>
        </Box>
      )}

      {/* RIGHT SIDE (Form + CTA) */}
      <Box
        sx={{
          width: isMobile ? "100%" : "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
        }}
      >
        <Box
          style={{
            width: "100%",
            backgroundColor: "#fff",
            // backgroundImage: `url(${background})`,
            // backgroundSize: "cover",
            // backgroundPosition: "center",
            // backgroundRepeat: "no-repeat",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div>
            <img src={Logo} width={"140px"} />
          </div>
          <Container>
            <Box textAlign="center">
              <Typography
                sx={{
                  fontSize: currentView === "landing" ? "44px" : "32px",
                  lineHeight: "40px",
                  margin: currentView === "landing" ? "10px" : "0px",
                }}
                className="clash-500"
              >
                {currentView === "landing" && (
                  <>
                    Level up your
                    <br />
                    workflow with chatbot
                  </>
                )}

                {currentView === "login" && "Login to your account"}

                {currentView === "signup" && (
                  <>
                    Create an account
                    <br />
                    to get started
                  </>
                )}
              </Typography>


              {(
                <div
                  style={{
                    background: "transparent",
                    boxShadow: "none",
                  }}
                >
                  <Box
                    sx={{
                      background: "transparent",
                      minHeight: "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: { xs: 1, sm: 2 },
                    }}
                  >
                    <Card
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        backgroundColor: lightBg2,
                        borderRadius: { xs: "20px", sm: "20px" },
                        borderColor: "#7b7de52e",
                        // boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                      }}
                    >
                      <CardContent sx={{ p: { xs: 3, sm: 3 } }}>
                        {currentView === "login" ? (
                          <Box
                            component="form"
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TextField
                              fullWidth
                              label="Email Address"
                              type="email"
                              size="medium"
                              placeholder="Enter your email"
                              value={loginForm.email}
                              error={!!loginErrors.email}
                              helperText={loginErrors.email}
                              onChange={(e) => {
                                setLoginForm({ ...loginForm, email: e.target.value });
                                if (loginErrors.email) setLoginErrors({ ...loginErrors, email: "" });
                              }}
                            />

                            <TextField
                              fullWidth
                              label="Password"
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter your password"
                              size="medium"
                              value={loginForm.password}
                              error={!!loginErrors.password}
                              helperText={loginErrors.password}
                              onChange={(e) => {
                                setLoginForm({ ...loginForm, password: e.target.value });
                                if (loginErrors.password) setLoginErrors({ ...loginErrors, password: "" });
                              }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() =>
                                        setShowPassword(!showPassword)
                                      }
                                      sx={{ color: "#666" }}
                                      edge="end"
                                    >
                                      {showPassword ? (
                                        <VisibilityOffIcon />
                                      ) : (
                                        <VisibilityIcon />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />

                            <Typography
                              onClick={() => setCurrentView("forgetpassword")}
                              sx={{
                                color: "#6366f1",
                                fontSize: "14px",
                                textAlign: "left",
                                cursor: "pointer",
                                fontWeight: 400,
                                "&:hover": {
                                  textDecoration: "underline",
                                  textDecorationColor: "#6366f1",
                                },
                              }}
                            >
                              Forgot password?
                            </Typography>
                            <Button
                              fullWidth
                              variant="contained"
                              size="large"
                              onClick={handleSignIn}
                              sx={{
                                backgroundColor: "#6366f1",
                                borderRadius: "8px",
                                padding: "14px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                textTransform: "none",
                                mt: 1,
                                "&:hover": { backgroundColor: "#5855eb" },
                              }}
                            >
                              {isLoading === true ? (
                                <CircularProgress
                                  color="#5855eb"
                                  size={"24px"}
                                />
                              ) : (
                                "Log In"
                              )}
                            </Button>
                          </Box>
                        ) : currentView === "signup" ? (
                          <Box
                            component="form"
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TextField
                              fullWidth
                              label="Name"
                              placeholder="Enter your name"
                              size="small"
                              value={signupForm.name}
                              error={!!signupErrors.name}
                              helperText={signupErrors.name}
                              onChange={(e) => {
                                setSignupForm({ ...signupForm, name: e.target.value });
                                if (signupErrors.name) setSignupErrors({ ...signupErrors, name: "" });
                              }}
                            />
                            <TextField
                              fullWidth
                              label="Email Address"
                              type="email"
                              placeholder="Enter your email"
                              value={signupForm.email}
                              error={!!signupErrors.email}
                              helperText={signupErrors.email}
                              onChange={(e) => {
                                setSignupForm({ ...signupForm, email: e.target.value });
                                if (signupErrors.email) setSignupErrors({ ...signupErrors, email: "" });
                              }}
                            />

                            <TextField
                              fullWidth
                              label="Password"
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter your password"
                              value={signupForm.password}
                              error={!!signupErrors.password}
                              helperText={signupErrors.password}
                              onChange={(e) => {
                                setSignupForm({ ...signupForm, password: e.target.value });
                                if (signupErrors.password) setSignupErrors({ ...signupErrors, password: "" });
                              }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() =>
                                        setShowPassword(!showPassword)
                                      }
                                      sx={{ color: "#666" }}
                                      edge="end"
                                    >
                                      {showPassword ? (
                                        <VisibilityOffIcon />
                                      ) : (
                                        <VisibilityIcon />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}

                            />
                            <TextField
                              fullWidth
                              label="Re-enter Password"
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder="Re-enter your password"
                              value={signupForm.confirmPassword}
                              error={!!signupErrors.confirmPassword}
                              helperText={signupErrors.confirmPassword}
                              onChange={(e) => {
                                setSignupForm({ ...signupForm, confirmPassword: e.target.value });
                                if (signupErrors.confirmPassword)
                                  setSignupErrors({ ...signupErrors, confirmPassword: "" });
                              }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() =>
                                        setShowConfirmPassword(
                                          !showConfirmPassword,
                                        )
                                      }
                                      sx={{ color: "#666" }}
                                      edge="end"
                                    >
                                      {showConfirmPassword ? (
                                        <VisibilityOffIcon />
                                      ) : (
                                        <VisibilityIcon />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}

                            />
                            <Button
                              fullWidth
                              variant="contained"
                              size="large"
                              onClick={handleSignUp}
                              sx={{
                                backgroundColor: "#6366f1",
                                borderRadius: "8px",
                                padding: "14px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                height: "50px",
                                textTransform: "none",
                                mt: 1,
                                "&:hover": { backgroundColor: "#5855eb" },
                              }}
                            >
                              {isLoading === true ? (
                                <CircularProgress
                                  color="#5855eb"
                                  size={"24px"}
                                />
                              ) : (
                                "Sign up"
                              )}
                            </Button>
                          </Box>
                        ) : (
                          <Box
                            component="form"
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TextField
                              fullWidth
                              label="Email Address"
                              type="email"
                              size="medium"
                              placeholder="Enter your email"
                              value={loginForm.email}
                              error={!!forgotErrors.email}
                              helperText={forgotErrors.email}
                              onChange={(e) => {
                                setLoginForm({ ...loginForm, email: e.target.value });
                                if (forgotErrors.email) setForgotErrors({ ...forgotErrors, email: "" });
                              }}
                            />
                            <Button
                              fullWidth
                              variant="contained"
                              size="large"
                              onClick={handleForgotPassword}
                              sx={{
                                backgroundColor: "#6366f1",
                                borderRadius: "8px",
                                padding: "14px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                textTransform: "none",
                                mt: 1,
                                "&:hover": { backgroundColor: "#5855eb" },
                              }}
                            >
                              {isLoading === true ? (
                                <CircularProgress
                                  color="#5855eb"
                                  size={"24px"}
                                />
                              ) : (
                                "Forget Password"
                              )}
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Box>

                  <Typography
                    variant="body2"
                    className="class-regular"
                    sx={{ textAlign: "center" }}
                  >
                    {currentView === "login"
                      ? "Don't have an account?"
                      : currentView === "signup"
                        ? "Already have an account?"
                        : "Back to"}{" "}
                    <Button
                      variant="text"
                      onClick={() =>
                        setCurrentView(
                          currentView === "login" ? "signup" : "login",
                        )
                      }
                      sx={{
                        color: "#6366f1",
                        textDecoration: "underline",
                        textTransform: "none",
                        p: 0,
                        minWidth: "auto",
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                    >
                      {currentView === "login" ? "Sign up" : "Login"}
                    </Button>
                  </Typography>
                </div>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default MedicalChatbotApp;
